import { format as dateFNSFormat } from "date-fns";
import {
  formatInTimeZone as dateFnsTzFormatInTimeZone,
  fromZonedTime,
} from "date-fns-tz";
import { APP_CONFIG } from "../constants/constants";
import { convertToTZForDateFnsNoHook } from "../hooks/timezone";

export const months = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

// Format date time to a specific format
// Reference: https://date-fns.org/v3.6.0/docs/format
type TDateTimeFormat = "default" | "yyyy-MM-dd" | "yyyy-MM-dd HH:mm (z)";
export const formattedDateTime = (
  date: string | number | Date,
  format: TDateTimeFormat | string = "default"
) => {
  if (!date) return "";
  if (date === "-") return "-";
  const dateInput =
    typeof date === "string" && date.match(/^\d{4}-\d{2}-\d{2}$/)
      ? `${date}T00:00:00`
      : date;
  let dateObj = new Date(dateInput);
  switch (format) {
    case "default":
      return dateFNSFormat(dateObj, "LLL dd, yyyy h:mmaaa (z)"); // "MM/dd/yyyy h:mmaaa (z)"
    case "yyyy-MM-dd": // instead of new Date(values.creation_date).toISOString().slice(0, 10)
    case "yyyy-MM-dd HH:mm (z)": // instead of new Date(values.creation_date).toISOString().slice(0, 16)
    default:
      return dateFNSFormat(dateObj, format);
  }
};

/**
 * Utility function to format the datetime for given timezone
 * @param date date need to be formatted
 * @param timezone timezone for formatting the date
 * @param format output format of the date
 * @returns {string} The formatted date time string
 */
export const formattedDateTimeBasedOnTimezone = (
  date: string | number | Date,
  timezone: string | null,
  format: string = "LLL dd, yyyy h:mmaa"
) => {
  if (!date) return "";
  if (date === "-") return "-";
  if (timezone) {
    return dateFnsTzFormatInTimeZone(
      date,
      convertToTZForDateFnsNoHook(timezone),
      format
    );
  } else {
    return formattedDateTime(date, format);
  }
};
export const convertDBDateToUSFormatDate_NoDateFunc = (dateStr: string) => {
  const [year, month, date] = dateStr.split("-");
  return `${month}/${date}/${year}`;
};
export const convertToAmPmWith24Hr_NoDateFunc = (timeStr: string | null) => {
  if (!timeStr) return "";
  const [hours, minutes, seconds] = timeStr.split(":").map(Number);
  const formattedHours = hours.toString().padStart(2, "0");
  const suffix = hours >= 12 ? "PM" : "AM";
  return `${formattedHours}:${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")} ${suffix}`;
};

export const jsTimeToDBTime = (time: number) => {
  return time ? Math.floor(new Date(time).getTime() / 1000) : 0;
};

export const dbTimeToJsTime = (time: number) => {
  return time ? time * 1000 : 0;
};

// source: https://stackoverflow.com/a/8212878/1268350
export const humanReadableTimeDuration = (
  milliseconds: number,
  compact: boolean = true
) => {
  // TIP: to find current time in milliseconds, use:
  // var  current_time_milliseconds = new Date().getTime();

  function numberEnding(number: number) {
    return number === 1 ? "" : "s";
  }

  let temp = Math.floor(milliseconds / 1000);
  let years = Math.floor(temp / 31536000);
  if (years) {
    return years + (compact ? "y" : " year" + numberEnding(years));
  }
  // months and weeks need testing
  // var months = Math.floor((temp %= 31536000) / 2592000);
  // if (months) {
  //   return months + (compact ? 'mo' : ' month' + numberEnding(months));
  // }
  // var weeks = Math.floor((temp %= 2592000) / 604800);
  // if (weeks) {
  //   return weeks + (compact ? 'w' : ' week' + numberEnding(weeks));
  // }
  var days = Math.floor((temp %= 31536000) / 86400);
  if (days) {
    return days + (compact ? "d" : " day" + numberEnding(days));
  }
  var hours = Math.floor((temp %= 86400) / 3600);
  if (hours) {
    return hours + (compact ? "h" : " hour" + numberEnding(hours));
  }
  var minutes = Math.floor((temp %= 3600) / 60);
  if (minutes) {
    return minutes + (compact ? "m" : " minute" + numberEnding(minutes));
  }
  var seconds = temp % 60;
  if (seconds) {
    return seconds + (compact ? "s" : " second" + numberEnding(seconds));
  }
  return compact ? "now" : "less than a second";
};

// Gregorian calendar start: October 15, 1582
const GREGORIAN_START_DATE = new Date(1582, 9, 15); // Month is 0-indexed, so 9 = October

export const gregorianDaysToJsDate = (numDays: number) => {
  const newDate = new Date(GREGORIAN_START_DATE);
  newDate.setDate(newDate.getDate() + numDays); // Add/subtract days
  return newDate;
};

export const calculateGregorianDate = (
  numDays: number,
  options: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "long",
    day: "numeric",
  }
) => {
  const newDate = gregorianDaysToJsDate(numDays);

  // Format the calculated date to a readable format (e.g., October 20, 1582)
  const formattedDate = newDate.toLocaleDateString("en-US", options);

  return formattedDate;
};

export const dateToGregorianDays = (date: string | Date) => {
  const gregorianDate = typeof date === "string" ? new Date(date) : date;
  const daysDifference = Math.round(
    (gregorianDate.getTime() - GREGORIAN_START_DATE.getTime()) /
      (1000 * 60 * 60 * 24)
  );

  return daysDifference;
};

export const convertUnixTimestampToDate = (timestamp: number) => {
  // Convert timestamp (seconds) to milliseconds and create a Date object
  const date = new Date(timestamp * 1000);

  // Format the date to a readable string
  const formattedDate = date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });

  return formattedDate;
};

export const getStartEndOfDayInUTC = (
  selected_date: string,
  timezone: string
) => {
  const refDateStringStartOfDay = `${selected_date}T00:00:00`;
  const startOfDayInUTC = fromZonedTime(
    refDateStringStartOfDay,
    convertToTZForDateFnsNoHook(timezone)
  );
  // if (APP_CONFIG.DEBUG.ALL)
  //   console.log(
  //     "ASSETFILT StartOfDayInUTC",
  //     jsTimeToDBTime(startOfDayInUTC.getTime())
  //   );
  const refDateStringEndOfDay = `${selected_date}T23:59:59`;
  const endOfDayInUTC = fromZonedTime(
    refDateStringEndOfDay,
    convertToTZForDateFnsNoHook(timezone)
  );
  // if (APP_CONFIG.DEBUG.ALL)
  //   console.log(
  //     "ASSETFILT EndOfDayInUTC",
  //     jsTimeToDBTime(endOfDayInUTC.getTime())
  //   );

  return { startOfDayInUTC, endOfDayInUTC };
};

export const get30DaysEarlierDateInUTC = (
  selected_date: string,
  timezone: string
) => {
  const refDateStringStart = `${selected_date}T00:00:00`;
  const startOfTimelineInUTC = fromZonedTime(
    refDateStringStart,
    convertToTZForDateFnsNoHook(timezone)
  );

  const earlierDate = new Date(startOfTimelineInUTC);
  earlierDate.setUTCDate(earlierDate.getUTCDate() - 30);

  return { earlierDate };
};
